import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { TestTube, Play, CheckCircle, XCircle } from 'lucide-react';
import { normalizeText, calculateContainsConfidence } from '@/lib/textNormalization';

interface TestResult {
  action_type: string;
  action_label: string;
  phrase_pattern: string;
  match_type: string;
  confidence_threshold: number;
  matched: boolean;
  confidence_score: number;
}

export default function PhraseTestInterface() {
  const { toast } = useToast();
  const [testText, setTestText] = useState('');
  const [testResults, setTestResults] = useState<TestResult[]>([]);
  const [testing, setTesting] = useState(false);

  const testPhraseMatching = async () => {
    if (!testText.trim()) {
      toast({
        title: "Error",
        description: "Please enter some text to test",
        variant: "destructive",
      });
      return;
    }

    setTesting(true);
    try {
      // Fetch all enabled canonical phrases with their action types
      const { data: phrases, error } = await supabase
        .from('canonical_phrases')
        .select(`
          id,
          phrase_pattern,
          match_type,
          confidence_threshold,
          enabled,
          admin_action_types (
            action_type,
            label
          )
        `)
        .eq('enabled', true);

      if (error) throw error;

      const results: TestResult[] = [];
      const normalizedTestText = normalizeText(testText);

      phrases?.forEach(phrase => {
        if (!phrase.admin_action_types) return;

        let matched = false;
        let confidence_score = 0;

        switch (phrase.match_type) {
          case 'exact':
            const normalizedPattern = normalizeText(phrase.phrase_pattern);
            matched = normalizedTestText === normalizedPattern;
            confidence_score = matched ? 100 : 0;
            break;
          
          case 'contains':
            const normalizedPhrasePattern = normalizeText(phrase.phrase_pattern);
            matched = normalizedTestText.includes(normalizedPhrasePattern);
            if (matched) {
              confidence_score = calculateContainsConfidence(normalizedPhrasePattern, normalizedTestText);
            }
            break;
          
          case 'regex':
            try {
              const regex = new RegExp(phrase.phrase_pattern, 'i');
              matched = regex.test(testText);
              confidence_score = matched ? 90 : 0;
            } catch (e) {
              console.error('Invalid regex pattern:', phrase.phrase_pattern);
              confidence_score = 0;
            }
            break;
        }

        // Apply confidence threshold
        const finalMatched = matched && confidence_score >= phrase.confidence_threshold;

        results.push({
          action_type: phrase.admin_action_types.action_type,
          action_label: phrase.admin_action_types.label,
          phrase_pattern: phrase.phrase_pattern,
          match_type: phrase.match_type,
          confidence_threshold: phrase.confidence_threshold,
          matched: finalMatched,
          confidence_score: Math.round(confidence_score)
        });
      });

      // Sort by confidence score descending
      results.sort((a, b) => b.confidence_score - a.confidence_score);
      setTestResults(results);

    } catch (error) {
      console.error('Error testing phrase matching:', error);
      toast({
        title: "Error",
        description: "Failed to test phrase matching",
        variant: "destructive",
      });
    } finally {
      setTesting(false);
    }
  };

  const clearTest = () => {
    setTestText('');
    setTestResults([]);
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center space-x-2">
          <TestTube className="h-5 w-5" />
          <CardTitle>Phrase Pattern Test Tool</CardTitle>
        </div>
        <CardDescription>
          Test how your canonical phrases match against sample transcript text. 
          <span className="text-xs text-muted-foreground block mt-1">
            ℹ️ Exact and Contains matching ignores punctuation, capitalization, and smart quotes
          </span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="test-text">Sample Transcript Text</Label>
          <Textarea
            id="test-text"
            value={testText}
            onChange={(e) => setTestText(e.target.value)}
            placeholder="Paste a sample transcript or text to test phrase matching..."
            rows={4}
            className="font-mono text-sm"
          />
        </div>

        <div className="flex space-x-2">
          <Button 
            onClick={testPhraseMatching} 
            disabled={testing || !testText.trim()}
          >
            <Play className="h-4 w-4 mr-2" />
            {testing ? 'Testing...' : 'Test Phrases'}
          </Button>
          <Button 
            variant="outline" 
            onClick={clearTest}
            disabled={!testText && testResults.length === 0}
          >
            Clear
          </Button>
        </div>

        {testResults.length > 0 && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">Test Results</h4>
              <Badge variant="outline">
                {testResults.filter(r => r.matched).length} of {testResults.length} matched
              </Badge>
            </div>
            
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {testResults.map((result, index) => (
                <div
                  key={index}
                  className={`p-3 rounded-lg border ${
                    result.matched 
                      ? 'bg-green-50 border-green-200 dark:bg-green-950/20 dark:border-green-800' 
                      : 'bg-gray-50 border-gray-200 dark:bg-gray-950/20 dark:border-gray-800'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        {result.matched ? (
                          <CheckCircle className="h-4 w-4 text-green-600" />
                        ) : (
                          <XCircle className="h-4 w-4 text-gray-400" />
                        )}
                        <span className="text-sm font-medium">{result.action_label}</span>
                        <Badge variant="outline" className="text-xs">
                          {result.match_type}
                        </Badge>
                      </div>
                      <p className="text-xs font-mono bg-white dark:bg-gray-900 p-2 rounded mb-2 border">
                        "{result.phrase_pattern}"
                      </p>
                      <div className="flex items-center space-x-3 text-xs text-muted-foreground">
                        <span>Action: {result.action_type}</span>
                        <span>•</span>
                        <span>Threshold: {result.confidence_threshold}%</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <Badge 
                        variant={result.matched ? "default" : "secondary"}
                        className="text-xs"
                      >
                        {result.confidence_score}%
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}